import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UserPlus, CheckCircle, XCircle, PauseCircle, PlayCircle, Trash, Warning, ArrowClockwise, CaretRight, CaretDown } from "@phosphor-icons/react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import OperatorDetailDialog from "@/components/OperatorDetailDialog";
import { BulkEmailList } from "@/components/BulkEmailList";
import { VisibleServicesPicker, sameIdSet } from "@/components/VisibleServicesPicker";
import GoogleBadge from "@/components/GoogleBadge";
import InviteLinkPanel from "@/components/InviteLinkPanel";
import { useNotifications } from "@/lib/notifications";

const ROLES = ["desk_operator", "site_operator", "tech_operator", "accounting_operator", "admin"];

const TabBadge = ({ count }) => {
  if (!count) return null;
  return (
    <span
      className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold"
      data-testid="tab-notif-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [fasce, setFasce] = useState([]);
  const [plafonds, setPlafonds] = useState([]);
  const [profCategories, setProfCategories] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  // Iter140h: righe espanse per mostrare i sotto-utenti sotto ogni Master Pro
  const [expandedPros, setExpandedPros] = useState(() => new Set());
  const toggleProExpansion = (id) => {
    setExpandedPros(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "pending";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });
  const { countForUrl, refresh: refreshNotifications } = useNotifications();
  const [inviteForm, setInviteForm] = useState({
    email: "", first_name: "", last_name: "", role: "tech_operator",
    is_supervisor: false,
  });
  // Iter209bf — Modale approvazione con edit dati completi:
  // apertura dalla riga "in attesa", modifica campi (nome/cognome, email,
  // ragione sociale, VAT, PEC, SDI, indirizzo, telefoni…), poi plafond +
  // fascia obbligatori per approvare.
  const [approveUser, setApproveUser] = useState(null);
  const [approveHiddenSvc, setApproveHiddenSvc] = useState([]);
  const [approveForm, setApproveForm] = useState({ plafond_id: "", fascia_id: "" });
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  // Iter209bo — Modale dettagli professionista (readonly).
  const [proDetail, setProDetail] = useState(null);

  const load = useCallback(async () => {
    const [u, f, p, c] = await Promise.all([
      api.get("/users?include_deleted=true"), api.get("/fasce"), api.get("/plafonds"), api.get("/professional-categories")
    ]);
    setUsers(u.data); setFasce(f.data); setPlafonds(p.data); setProfCategories(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openApproveDialog = (u) => {
    const defaultFascia = fasce.find(f => f.is_default);
    const defaultPlafond = plafonds.find(p => p.is_default);
    setApproveForm({
      plafond_id: u.plafond_id || defaultPlafond?._id || "",
      fascia_id: u.fascia_id || defaultFascia?._id || "",
    });
    // Iter209bf — snapshot dei dati modificabili prima dell'approvazione
    setEditForm({
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      email: u.email || "",
      phone: u.phone || "",
      company_name: u.company_name || "",
      company_insegna: u.company_insegna || "",
      company_vat: u.company_vat || "",
      company_pec: u.company_pec || "",
      company_sdi: u.company_sdi || "",
      company_email: u.company_email || "",
      company_phone: u.company_phone || "",
      company_toponimo: u.company_toponimo || "",
      company_address: u.company_address || "",
      company_civico: u.company_civico || "",
      company_city: u.company_city || "",
      owner_phone: u.owner_phone || "",
      owner_email: u.owner_email || "",
    });
    setApproveUser(u);
    setApproveHiddenSvc(u.hidden_service_ids || []);
  };

  const persistEditIfDirty = async () => {
    if (!approveUser) return true;
    const dirty = Object.entries(editForm).reduce((acc, [k, v]) => {
      if ((approveUser[k] || "") !== (v || "")) acc[k] = v;
      return acc;
    }, {});
    if (Object.keys(dirty).length === 0) return true;
    setSavingEdit(true);
    try {
      await api.patch(`/users/${approveUser.id}`, dirty);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio dati");
      return false;
    } finally { setSavingEdit(false); }
  };

  const saveEditOnly = async () => {
    const ok = await persistEditIfDirty();
    if (!ok) return;
    toast.success("Dati salvati");
    await load();
    setApproveUser(null);
  };

  const submitApprove = async () => {
    if (!approveForm.plafond_id) return toast.error("Seleziona il plafond");
    if (!approveForm.fascia_id) return toast.error("Seleziona la fascia di prezzo");
    // Iter209bf — salva prima le modifiche ai dati anagrafici
    const ok = await persistEditIfDirty();
    if (!ok) return;
    try {
      await api.post(`/users/${approveUser.id}/approve`, approveForm);
      if (!sameIdSet(approveHiddenSvc, approveUser.hidden_service_ids)) {
        await api.patch(`/users/${approveUser.id}`, { hidden_service_ids: approveHiddenSvc });
      }
      toast.success("Professionista approvato");
      setApproveUser(null);
      // Iter209av — Ricarica sia la lista utenti sia il contatore
      // notifiche così il badge in sidebar/tab si aggiorna subito.
      await Promise.all([load(), refreshNotifications?.()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore approvazione");
    }
  };

  const approve = async (id) => {
    // Fallback per operatori (non professionisti): approva diretto
    await api.post(`/users/${id}/approve`);
    toast.success("Approvato");
    // Iter209av — Aggiorna anche il badge notifiche.
    await Promise.all([load(), refreshNotifications?.()]);
  };
  const reject = async (id) => {
    await api.post(`/users/${id}/reject`);
    toast.success("Rifiutato");
    // Iter209av — Aggiorna anche il badge notifiche.
    await Promise.all([load(), refreshNotifications?.()]);
  };
  const suspend = async (id) => { await api.post(`/users/${id}/suspend`); toast.success("Sospeso"); load(); };
  const reactivate = async (id) => { await api.post(`/users/${id}/reactivate`); toast.success("Riattivato"); load(); };
  const delUser = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Utente eliminato"); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } };

  const invite = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/users/invite", inviteForm);
      toast.success(`Invito creato. Password: ${r.data.temp_password}`);
      setShowInvite(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const updateUser = async (id, patch) => {
    try { await api.patch(`/users/${id}`, patch); toast.success("Aggiornato"); load(); }
    catch (err) { toast.error("Errore"); }
  };

  const [showDeletedDialog, setShowDeletedDialog] = useState(false);

  // Utenti in stato `deleted` (tombstone soft-delete GDPR) esclusi da tutte
  // le liste UI. Il backend applica lo stesso filtro di default in `/users`.
  const visible = users.filter(u => u.status !== "deleted");
  const deletedUsers = users.filter(u => u.status === "deleted");
  const pending = visible.filter(u => u.status === "pending_approval");
  const operators = visible.filter(u => u.role !== "professional" && u.status !== "pending_approval");
  const professionals = visible.filter(u => u.role === "professional" && u.status !== "pending_approval");
  // Iter140h — raggruppamento Master Pro / Sotto-utenti (collaboratori).
  // - Master Pro: `parent_user_id` assente / null.
  // - Sotto-utenti: `parent_user_id` puntante a un master presente nella
  //   lista `professionals` (ID confrontato con `u.id` o `u._id`).
  const masterPros = professionals.filter(u => !u.parent_user_id);
  const subUsersByParent = React.useMemo(() => {
    const map = new Map();
    for (const u of professionals) {
      const parentId = u.parent_user_id;
      if (!parentId) continue;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId).push(u);
    }
    // Ordina i figli per nome (deterministico).
    for (const arr of map.values()) {
      arr.sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
    }
    return map;
  }, [professionals]);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-eyebrow">Amministrazione</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Utenti</h1>
          <p className="text-muted-foreground mt-2">Gestione operatori, professionisti e approvazioni</p>
          {deletedUsers.length > 0 && (
            <button
              type="button"
              onClick={() => setShowDeletedDialog(true)}
              className="mt-3 inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 border border-slate-200 transition-colors"
              data-testid="deleted-users-indicator"
              title="Audit trail GDPR"
            >
              <Trash size={12} weight="duotone" />
              {deletedUsers.length} utent{deletedUsers.length === 1 ? "e eliminato" : "i eliminati"} (GDPR)
              <span className="underline">Mostra</span>
            </button>
          )}
        </div>
        <Button className="ht-gradient border-0" onClick={() => setShowInvite(true)}
          data-testid="invite-btn"
          style={{ visibility: activeTab === "professionals" ? "hidden" : "visible" }}>
          <UserPlus size={16} className="mr-2" weight="bold" /> Invita Operatore
        </Button>
      </div>

      {/* Dialog audit trail utenti eliminati (soft-delete GDPR). Sola lettura. */}
      <Dialog open={showDeletedDialog} onOpenChange={setShowDeletedDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Utenti eliminati (audit GDPR)</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground mb-3">
            Tombstone soft-delete conservati per il ciclo di conservazione. Sola lettura.
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y" data-testid="deleted-users-list">
            {deletedUsers.length === 0 && (
              <div className="text-sm text-slate-400 italic py-4">Nessun utente eliminato.</div>
            )}
            {deletedUsers.map(u => (
              <div key={u.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`deleted-user-${u.id}`}>
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs font-bold">
                  {(u.first_name?.[0] || "?").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{u.first_name} {u.last_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email} · {u.role}</div>
                </div>
                {u.deleted_at && (
                  <div className="text-xs text-muted-foreground shrink-0">
                    {new Date(u.deleted_at).toLocaleDateString("it-IT")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            {/* Iter209av — Il contatore è la SORGENTE-DI-VERITÀ del
                backend (`pending.length` derivato da GET /users?
                include_deleted=true). Il TabBadge si azzera
                automaticamente quando `pending.length === 0` (il
                componente ritorna null). */}
            Approvazioni{pending.length > 0 ? ` (${pending.length})` : ""}
            <TabBadge count={pending.length} />
          </TabsTrigger>
          <TabsTrigger value="operators">
            Operatori ({operators.length})
            <TabBadge count={countForUrl("/users?tab=operators")} />
          </TabsTrigger>
          <TabsTrigger value="professionals">
            Professionisti ({professionals.length})
            <TabBadge count={countForUrl("/users?tab=professionals")} />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <div className="kpi-card">
            {pending.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Nessuna richiesta in attesa</p>
            ) : (
              <table className="w-full text-sm mobile-cards">
                <thead><tr className="text-left border-b">
                  <th className="py-2">Nome</th><th>Email</th><th>Azienda</th><th className="text-right">Azioni</th>
                </tr></thead>
                <tbody>
                  {pending.map(u => (
                    <tr key={u.id} className="border-b hover:bg-[color:hsl(var(--surface-alt))]" data-testid={`pending-${u.id}`}>
                      <td data-label="Nome" className="py-3 font-medium">
                        <button
                          type="button"
                          onClick={() => u.role === "professional" ? openApproveDialog(u) : approve(u.id)}
                          className="text-left hover:underline text-primary"
                          data-testid={`open-pending-${u.id}`}
                        >
                          {u.first_name} {u.last_name}
                        </button>
                      </td>
                      <td data-label="Email">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          {u.email}
                          <GoogleBadge user={u} />
                        </span>
                      </td>
                      <td data-label="Azienda">{u.company_name || "—"}</td>
                      <td data-label="Azioni" className="text-right space-x-1 mobile-actions">
                        <Button size="sm" variant="outline"
                          onClick={() => u.role === "professional" ? openApproveDialog(u) : approve(u.id)}
                          data-testid={`approve-${u.id}`}>
                          <CheckCircle size={14} className="mr-1" color="#166534" /> {u.role === "professional" ? "Dettagli" : "Approva"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(u.id)}>
                          <XCircle size={14} className="mr-1" color="#991B1B" /> Rifiuta
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="operators">
          {/* Iter209am — Sezione invito operatori tramite link */}
          <div className="mb-6">
            <InviteLinkPanel variant="admin" testId="admin-invite-panel" />
          </div>
          <div className="kpi-card">
            <table className="w-full text-sm mobile-cards">
              <thead><tr className="text-left border-b">
                <th className="py-2">Nome</th><th>Email</th><th>Ruolo</th><th>Tipo</th><th>Sup.</th><th>Stato</th><th></th>
              </tr></thead>
              <tbody>
                {operators.map(u => (
                  <tr key={u.id} className="border-b hover:bg-[color:hsl(var(--surface-alt))]" data-testid={`operator-row-${u.id}`}>
                    <td data-label="Nome" className="py-2">
                      <button onClick={() => setDetailUser(u)} className="font-medium text-left hover:underline text-primary" data-testid={`open-operator-${u.id}`}>
                        {u.first_name} {u.last_name}
                      </button>
                    </td>
                    <td data-label="Email">{u.email}</td>
                    <td data-label="Ruolo">
                      <select className="border rounded px-2 py-1 text-xs" value={u.role}
                        onChange={e => updateUser(u.id, { role: e.target.value })}>
                        {ROLES.map(r => <option key={r}>{r}</option>)}
                      </select>
                    </td>
                    <td data-label="Tipo">
                      <select className="border rounded px-2 py-1 text-xs" value={u.operator_type || "in"}
                        onChange={e => updateUser(u.id, { operator_type: e.target.value })}
                        data-testid={`operator-type-${u.id}`}>
                        <option value="in">IN (dipendente)</option>
                        <option value="out">OUT (esterno)</option>
                      </select>
                    </td>
                    <td data-label="Supervisor">
                      <input type="checkbox" checked={u.is_supervisor}
                        onChange={e => updateUser(u.id, { is_supervisor: e.target.checked })} />
                    </td>
                    <td data-label="Stato">
                      <span className="role-badge" style={{
                        background: u.status === "approved" ? "#dcfce7" :
                                    u.status === "onboarding" ? "#fef3c7" : "#fee2e2"
                      }}>{u.status}</span>
                      {u.status === "onboarding" && u.invite_expires_at && (
                        <div className="text-[10px] text-amber-700 mt-1">
                          Scade il {new Date(u.invite_expires_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                    </td>
                    <td className="mobile-actions">
                      <div className="flex gap-1 justify-end">
                        {u.status === "onboarding" && (
                          <Button size="sm" variant="ghost" title="Reinvia invito"
                            data-testid={`resend-invite-${u.id}`}
                            onClick={async () => {
                              try {
                                await api.post(`/users/${u.id}/resend-invite`);
                                toast.success("Invito rinviato via email");
                                load();
                              } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
                            }}>
                            <ArrowClockwise size={14} color="#4C1D95" weight="bold" />
                          </Button>
                        )}
                        <DoubleConfirmAction
                          trigger={u.status === "approved"
                            ? <Button size="sm" variant="ghost" title="Sospendi" data-testid={`suspend-${u.id}`}><PauseCircle size={16} /></Button>
                            : <Button size="sm" variant="ghost" title="Riattiva" data-testid={`reactivate-${u.id}`}><PlayCircle size={16} color="#166534" weight="fill" /></Button>
                          }
                          title={u.status === "approved" ? "Sospendere questo operatore?" : "Riattivare questo operatore?"}
                          description={u.status === "approved"
                            ? `L'operatore ${u.first_name} ${u.last_name} non potrà più accedere alla piattaforma. Le pratiche in corso resteranno assegnate ma verranno flaggate come critiche.`
                            : `${u.first_name} ${u.last_name} potrà nuovamente accedere alla piattaforma e ricevere assegnazioni.`}
                          confirmLabel={u.status === "approved" ? "Sospendi" : "Riattiva"}
                          onConfirm={() => u.status === "approved" ? suspend(u.id) : reactivate(u.id)}
                          destructive={u.status === "approved"}
                        />
                        <DoubleConfirmAction
                          trigger={<Button size="sm" variant="ghost" title="Elimina" data-testid={`delete-${u.id}`}><Trash size={14} color="#991B1B" /></Button>}
                          title="Eliminare definitivamente questo operatore?"
                          description={`L'operatore ${u.first_name} ${u.last_name} verrà rimosso in modo permanente. Digita "ELIMINA" per confermare.`}
                          confirmLabel="Elimina definitivamente"
                          onConfirm={() => delUser(u.id)}
                          destructive
                          requireTypeConfirm="ELIMINA"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="professionals">
          <div className="kpi-card">
            <table className="w-full text-sm mobile-cards">
              <thead><tr className="text-left border-b">
                <th className="py-2 w-8"></th>
                <th className="py-2">Nome</th><th>Email</th><th>Azienda</th><th>Categoria</th><th>Fascia</th><th>Plafond</th><th>Usato mese</th><th>Stato</th><th className="text-right">Azioni</th>
              </tr></thead>
              <tbody>
                {(() => {
                  // Iter140h: renderer riutilizzato per Master + Sotto-utenti.
                  const renderProRow = (u, opts = {}) => {
                    const { isSub = false, hasSubs = false, expanded = false } = opts;
                    return (
                      <tr
                        key={u.id}
                        className={`border-b ${isSub ? "bg-slate-50/70" : ""}`}
                        data-testid={`prof-row-${u.id}`}
                      >
                        <td className="py-2 pl-2 align-middle">
                          {hasSubs && !isSub ? (
                            <button
                              type="button"
                              onClick={() => toggleProExpansion(u.id)}
                              className="p-1 rounded hover:bg-purple-100 text-purple-700"
                              aria-label={expanded ? "Chiudi collaboratori" : "Apri collaboratori"}
                              data-testid={`prof-expand-${u.id}`}
                            >
                              {expanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                            </button>
                          ) : isSub ? (
                            <span className="inline-block w-4 border-l-2 border-purple-200 h-6 ml-3" aria-hidden />
                          ) : null}
                        </td>
                        <td
                          data-label="Nome"
                          className={`py-2 ${isSub ? "pl-6 text-slate-700 italic" : "font-medium"}`}
                        >
                          {isSub && <span className="text-purple-400 mr-1" aria-hidden>↳</span>}
                          {/* Iter209bo — Nome pro cliccabile → pannello dettagli readonly */}
                          <button
                            type="button"
                            onClick={() => setProDetail(u)}
                            className="text-left hover:underline text-primary font-medium"
                            data-testid={`prof-open-detail-${u.id}`}
                          >
                            {/* Iter209e9 — Ragione sociale come nome principale (fallback nome e cognome). */}
                            {(!isSub && (u.company_name || "").trim()) || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}
                          </button>
                          {isSub && (
                            <span
                              className="ml-2 text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded"
                              data-testid={`prof-sub-badge-${u.id}`}
                            >
                              Collaboratore
                            </span>
                          )}
                        </td>
                        <td data-label="Email">
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            {u.email}
                            <GoogleBadge user={u} />
                          </span>
                        </td>
                        <td data-label="Azienda">{u.company_name || "—"}</td>
                        <td data-label="Categoria">
                          <select className="border rounded px-2 py-1 text-xs" value={u.professional_category_id || ""}
                            onChange={e => updateUser(u.id, { professional_category_id: e.target.value })}
                            data-testid={`prof-cat-${u.id}`}>
                            <option value="">—</option>
                            {profCategories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                          </select>
                        </td>
                        <td data-label="Fascia">
                          <select className="border rounded px-2 py-1 text-xs" value={u.fascia_id || ""}
                            onChange={e => updateUser(u.id, { fascia_id: e.target.value })}>
                            {fasce.map(f => <option key={f._id} value={f._id}>{f.name} ({f.discount_percent}%)</option>)}
                          </select>
                        </td>
                        <td data-label="Plafond">
                          <select className="border rounded px-2 py-1 text-xs" value={u.plafond_id || ""}
                            onChange={e => updateUser(u.id, { plafond_id: e.target.value })}>
                            {plafonds.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                          </select>
                        </td>
                        <td data-label="Usato mese" className="font-mono">€{u.plafond_used_current_month?.toFixed(0) || 0}</td>
                        <td data-label="Stato">
                          <span className="role-badge" style={{ background: u.status === "approved" ? "#dcfce7" : "#fee2e2" }}>{u.status}</span>
                        </td>
                        <td data-label="Azioni" className="text-right mobile-actions">
                          <div className="flex gap-1 justify-end">
                            <DoubleConfirmAction
                              trigger={u.status === "approved"
                                ? <Button size="sm" variant="ghost" title="Sospendi" data-testid={`prof-suspend-${u.id}`}><PauseCircle size={16} /></Button>
                                : <Button size="sm" variant="ghost" title="Riattiva" data-testid={`prof-reactivate-${u.id}`}><PlayCircle size={16} color="#166534" weight="fill" /></Button>
                              }
                              title={u.status === "approved" ? "Sospendere questo professionista?" : "Riattivare questo professionista?"}
                              description={u.status === "approved"
                                ? `${u.first_name} ${u.last_name} (${u.email}) non potrà più accedere alla piattaforma. Le pratiche in corso resteranno visibili in sola lettura.`
                                : `${u.first_name} ${u.last_name} potrà nuovamente accedere e acquistare servizi dal marketplace.`}
                              confirmLabel={u.status === "approved" ? "Sospendi" : "Riattiva"}
                              onConfirm={() => u.status === "approved" ? suspend(u.id) : reactivate(u.id)}
                              destructive={u.status === "approved"}
                            />
                            {!isSub && (
                              <DoubleConfirmAction
                                trigger={<Button size="sm" variant="ghost" title="Elimina" data-testid={`prof-delete-${u.id}`}><Trash size={14} color="#991B1B" /></Button>}
                                title="Eliminare definitivamente questo professionista?"
                                description={`Il professionista ${u.first_name} ${u.last_name} (${u.email}) verrà rimosso in modo permanente. Digita "ELIMINA" per confermare.`}
                                confirmLabel="Elimina definitivamente"
                                onConfirm={() => delUser(u.id)}
                                destructive
                                requireTypeConfirm="ELIMINA"
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  };
                  const rows = [];
                  masterPros.forEach((master) => {
                    const subs = subUsersByParent.get(master.id) || [];
                    const hasSubs = subs.length > 0;
                    const expanded = expandedPros.has(master.id);
                    rows.push(renderProRow(master, { hasSubs, expanded }));
                    if (hasSubs && expanded) {
                      subs.forEach((sub) => rows.push(renderProRow(sub, { isSub: true })));
                    }
                  });
                  return rows;
                })()}
              </tbody>
            </table>
          </div>
          <BulkEmailList professionals={professionals} />
        </TabsContent>
      </Tabs>

      <OperatorDetailDialog
        user={detailUser}
        onClose={() => setDetailUser(null)}
        onSaved={load}
      />

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invita Operatore</DialogTitle></DialogHeader>
          <form onSubmit={invite} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nome</Label><Input required value={inviteForm.first_name} onChange={e => setInviteForm({ ...inviteForm, first_name: e.target.value })} /></div>
              <div><Label>Cognome</Label><Input required value={inviteForm.last_name} onChange={e => setInviteForm({ ...inviteForm, last_name: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label><Input type="email" required value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} /></div>
            <div><Label>Ruolo</Label>
              <select className="w-full h-10 border rounded px-3 text-sm" value={inviteForm.role} onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select></div>
            <p className="text-xs text-muted-foreground">Il tipo (IN/OUT) e i punteggi per servizio potranno essere impostati cliccando sull'operatore dopo la creazione.</p>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={inviteForm.is_supervisor} onChange={e => setInviteForm({ ...inviteForm, is_supervisor: e.target.checked })} />
              <Label>Badge Supervisor</Label>
            </div>
            <Button type="submit" className="w-full ht-gradient border-0">Crea invito</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Iter209bf — Dettagli richiesta + modifica dati prima dell'approvazione */}
      {/* Iter209bo — Dettagli professionista (readonly) */}
      <ProfessionalDetailDialog user={proDetail} onClose={() => setProDetail(null)} onSaved={load} fasce={fasce} plafonds={plafonds} profCategories={profCategories} />

      <Dialog open={!!approveUser} onOpenChange={o => { if (!o) setApproveUser(null); }}>
        <DialogContent data-testid="approve-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettagli richiesta di approvazione</DialogTitle>
          </DialogHeader>
          {approveUser && (
            <div className="space-y-5 py-2">
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                <div className="text-xs uppercase tracking-wider text-purple-800 font-semibold mb-1">
                  Richiesta in attesa
                </div>
                <div className="font-semibold text-sm">{approveUser.first_name} {approveUser.last_name}</div>
                <div className="text-xs text-muted-foreground">{approveUser.email}</div>
              </div>

              {/* Anagrafica titolare */}
              <section>
                <h4 className="text-sm font-semibold mb-2">Titolare</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <EditField label="Nome" testid="edit-first-name"
                    value={editForm.first_name}
                    onChange={(v) => setEditForm({ ...editForm, first_name: v })} />
                  <EditField label="Cognome" testid="edit-last-name"
                    value={editForm.last_name}
                    onChange={(v) => setEditForm({ ...editForm, last_name: v })} />
                  <EditField label="Email principale" type="email" testid="edit-email"
                    value={editForm.email}
                    onChange={(v) => setEditForm({ ...editForm, email: v })} />
                  <EditField label="Telefono principale" testid="edit-phone"
                    value={editForm.phone}
                    onChange={(v) => setEditForm({ ...editForm, phone: v })} />
                  <EditField label="Cellulare titolare" testid="edit-owner-phone"
                    value={editForm.owner_phone}
                    onChange={(v) => setEditForm({ ...editForm, owner_phone: v })} />
                  <EditField label="Email titolare" type="email" testid="edit-owner-email"
                    value={editForm.owner_email}
                    onChange={(v) => setEditForm({ ...editForm, owner_email: v })} />
                </div>
              </section>

              {/* Anagrafica azienda */}
              <section>
                <h4 className="text-sm font-semibold mb-2">Dati studio / azienda</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <EditField label="Ragione sociale" testid="edit-company-name"
                    value={editForm.company_name}
                    onChange={(v) => setEditForm({ ...editForm, company_name: v })} />
                  <EditField label="Insegna" testid="edit-company-insegna"
                    value={editForm.company_insegna}
                    onChange={(v) => setEditForm({ ...editForm, company_insegna: v })} />
                  <EditField label="P.IVA" testid="edit-company-vat"
                    value={editForm.company_vat}
                    onChange={(v) => setEditForm({ ...editForm, company_vat: v })} />
                  <EditField label="PEC" type="email" testid="edit-company-pec"
                    value={editForm.company_pec}
                    onChange={(v) => setEditForm({ ...editForm, company_pec: v })} />
                  <EditField label="Codice SDI (7 caratteri)" testid="edit-company-sdi"
                    value={editForm.company_sdi}
                    maxLength={7}
                    onChange={(v) => setEditForm({ ...editForm, company_sdi: (v || "").toUpperCase().slice(0, 7) })} />
                  <EditField label="Email azienda" type="email" testid="edit-company-email"
                    value={editForm.company_email}
                    onChange={(v) => setEditForm({ ...editForm, company_email: v })} />
                  <EditField label="Telefono azienda" testid="edit-company-phone"
                    value={editForm.company_phone}
                    onChange={(v) => setEditForm({ ...editForm, company_phone: v })} />
                </div>
              </section>

              {/* Indirizzo */}
              <section>
                <h4 className="text-sm font-semibold mb-2">Indirizzo</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <EditField label="Toponimo" testid="edit-toponimo"
                    value={editForm.company_toponimo}
                    onChange={(v) => setEditForm({ ...editForm, company_toponimo: v })} />
                  <div className="md:col-span-2">
                    <EditField label="Indirizzo" testid="edit-address"
                      value={editForm.company_address}
                      onChange={(v) => setEditForm({ ...editForm, company_address: v })} />
                  </div>
                  <EditField label="Civico" testid="edit-civico"
                    value={editForm.company_civico}
                    onChange={(v) => setEditForm({ ...editForm, company_civico: v })} />
                  <div className="md:col-span-4">
                    <EditField label="Città" testid="edit-city"
                      value={editForm.company_city}
                      onChange={(v) => setEditForm({ ...editForm, company_city: v })} />
                  </div>
                </div>
              </section>

              {/* Plafond & Fascia */}
              <section className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-2">Assegnazione plafond e fascia</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Obbligatori per approvare. Potrai modificarli in qualsiasi momento dalla scheda utente.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Plafond mensile</Label>
                    <select
                      value={approveForm.plafond_id}
                      onChange={e => setApproveForm({ ...approveForm, plafond_id: e.target.value })}
                      className="w-full mt-1 h-10 border rounded-md px-3 text-sm"
                      data-testid="approve-plafond-select">
                      <option value="">— Seleziona plafond —</option>
                      {plafonds.map(p => (
                        <option key={p._id} value={p._id}>
                          {p.name} · €{Number(p.monthly_limit || 0).toFixed(0)}/mese
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Fascia di prezzo</Label>
                    <select
                      value={approveForm.fascia_id}
                      onChange={e => setApproveForm({ ...approveForm, fascia_id: e.target.value })}
                      className="w-full mt-1 h-10 border rounded-md px-3 text-sm"
                      data-testid="approve-fascia-select">
                      <option value="">— Seleziona fascia —</option>
                      {fasce.map(f => (
                        <option key={f._id} value={f._id}>
                          {f.name} · sconto {f.discount_percent}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              <section className="border-t pt-3">
                <VisibleServicesPicker hiddenIds={approveHiddenSvc} onChange={setApproveHiddenSvc} testIdPrefix="approve" />
              </section>

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setApproveUser(null)} data-testid="approve-cancel">
                  Annulla
                </Button>
                <Button variant="outline" onClick={saveEditOnly}
                  disabled={savingEdit}
                  data-testid="approve-save-only">
                  {savingEdit ? "Salvataggio…" : "Salva modifiche"}
                </Button>
                <div className="flex-1" />
                <Button className="ht-gradient border-0" onClick={submitApprove}
                  disabled={!approveForm.plafond_id || !approveForm.fascia_id || savingEdit}
                  data-testid="approve-confirm">
                  <CheckCircle size={14} className="mr-2" /> Salva e approva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DoubleConfirmAction({ trigger, title, description, confirmLabel, onConfirm, destructive = false, requireTypeConfirm = null }) {
  const [open, setOpen] = React.useState(false);
  const [typed, setTyped] = React.useState("");
  const canConfirm = !requireTypeConfirm || typed === requireTypeConfirm;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTyped(""); }}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Warning size={18} weight="fill" color={destructive ? "#991B1B" : "#B45309"} />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-2">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {requireTypeConfirm && (
          <div className="mt-2">
            <input value={typed} onChange={e => setTyped(e.target.value)}
              placeholder={`Digita "${requireTypeConfirm}" per confermare`}
              className="w-full h-10 border rounded px-3 text-sm font-mono"
              data-testid="confirm-type-input" />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canConfirm}
            onClick={async () => { await onConfirm(); setOpen(false); setTyped(""); }}
            className={destructive ? "bg-red-600 hover:bg-red-700" : ""}
            data-testid="confirm-action">
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Iter209bf — Campo editabile compatto usato nel modale di approvazione.
function EditField({ label, value, onChange, type = "text", testid, maxLength }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        data-testid={testid}
        className="h-9 text-sm"
      />
    </div>
  );
}


// Iter209bo — Modale dettagli professionista (dati editabili dal 209e8).
// Mostra tutti i dati salvati sul profilo (registrazione + approvazione)
// in blocchi ordinati: Titolare, Dati studio/azienda, Indirizzo,
// Assegnazioni commerciali (categoria/fascia/plafond), Consumo e stato.
// Iter209e8 — Campi anagrafici/studio/sede editabili (PATCH solo del diff).
const PRO_EDIT_FIELDS = [
  "first_name", "last_name", "email", "phone", "owner_phone", "owner_email",
  "company_name", "company_vat", "company_pec", "company_sdi", "company_email", "company_phone",
  "company_toponimo", "company_address", "company_civico", "company_city",
  "company_province", "company_cap", "company_country",
];
const buildProEditForm = (u) => Object.fromEntries(PRO_EDIT_FIELDS.map((k) => [k, (u && u[k]) || ""]));

function ProfessionalDetailDialog({ user, onClose, onSaved, fasce = [], plafonds = [], profCategories = [] }) {
  const [form, setForm] = useState(() => buildProEditForm(user));
  const [saving, setSaving] = useState(false);
  const [hiddenSvc, setHiddenSvc] = useState(() => user?.hidden_service_ids || []);
  useEffect(() => { setForm(buildProEditForm(user)); setHiddenSvc(user?.hidden_service_ids || []); }, [user]);
  if (!user) return null;
  const fascia = fasce.find(f => f._id === user.fascia_id);
  const plafond = plafonds.find(p => p._id === user.plafond_id);
  const category = profCategories.find(c => c._id === user.professional_category_id);
  const line = (label, value) => (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm break-words">{value || <span className="text-slate-400">—</span>}</span>
    </div>
  );
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const field = (k, label, extra = {}) => (
    <EditField label={label} value={form[k]} onChange={set(k)} testid={`pro-detail-edit-${k}`} {...extra} />
  );
  const dirty = Object.entries(form).reduce((acc, [k, v]) => {
    if ((user[k] || "") !== (v || "")) acc[k] = v;
    return acc;
  }, {});
  const hiddenDirty = !sameIdSet(hiddenSvc, user.hidden_service_ids);
  const isDirty = Object.keys(dirty).length > 0 || hiddenDirty;
  const save = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      await api.patch(`/users/${user.id || user._id}`, hiddenDirty ? { ...dirty, hidden_service_ids: hiddenSvc } : dirty);
      toast.success("Dati professionista aggiornati");
      await onSaved?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore salvataggio dati");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pro-detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dettaglio professionista</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2 text-sm">
          {/* Header */}
          <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 flex items-start gap-3">
            <div className="flex-1">
              <div className="font-semibold" data-testid="pro-detail-name">
                {user.first_name} {user.last_name}
              </div>
              <div className="text-xs text-muted-foreground" data-testid="pro-detail-email">{user.email}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{user.status}</span>
                {user.is_supervisor && (
                  <span className="text-[10px] uppercase tracking-wider bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded">Supervisor</span>
                )}
                {user.parent_user_id && (
                  <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Collaboratore</span>
                )}
              </div>
            </div>
          </div>

          {/* Titolare */}
          <section>
            <h4 className="font-semibold mb-2">Titolare</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pro-detail-owner">
              {field("first_name", "Nome")}
              {field("last_name", "Cognome")}
              {field("email", "Email", { type: "email" })}
              {field("phone", "Telefono")}
              {field("owner_phone", "Cellulare titolare")}
              {field("owner_email", "Email titolare", { type: "email" })}
            </div>
          </section>

          {/* Studio / azienda */}
          <section>
            <h4 className="font-semibold mb-2">Dati studio / azienda</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pro-detail-company">
              {field("company_name", "Ragione sociale")}
              {field("company_vat", "Partita IVA", { maxLength: 13 })}
              {field("company_pec", "PEC", { type: "email" })}
              <div>
                {field("company_sdi", "Codice SDI", { maxLength: 7 })}
                {user.company_sdi_not_available && (
                  <div className="text-[11px] text-slate-500 mt-1">SDI dichiarato non applicabile</div>
                )}
              </div>
              {field("company_email", "Email azienda", { type: "email" })}
              {field("company_phone", "Telefono azienda")}
            </div>
          </section>

          {/* Sede legale */}
          <section>
            <h4 className="font-semibold mb-2">Sede legale</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="pro-detail-address">
              {field("company_toponimo", "Toponimo")}
              <div className="md:col-span-2">{field("company_address", "Indirizzo")}</div>
              {field("company_civico", "Civico")}
              {field("company_city", "Città")}
              {field("company_province", "Provincia", { maxLength: 2 })}
              {field("company_cap", "CAP", { maxLength: 5 })}
              {field("company_country", "Paese")}
            </div>
          </section>

          {/* Assegnazioni commerciali */}
          <section>
            <h4 className="font-semibold mb-2">Assegnazioni commerciali</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pro-detail-commercial">
              {line("Categoria professionista", category?.name)}
              {line("Fascia di prezzo", fascia
                ? `${fascia.name} · sconto ${fascia.discount_percent}%`
                : null)}
              {line("Plafond mensile", plafond
                ? `${plafond.name} · €${Number(plafond.monthly_limit || 0).toFixed(0)}/mese`
                : null)}
              {line("Utilizzato mese corrente",
                typeof user.plafond_used_current_month === "number"
                  ? `€${user.plafond_used_current_month.toFixed(2)}`
                  : null)}
              {line("Credito", typeof user.credit_balance === "number"
                ? `€${user.credit_balance.toFixed(2)}`
                : null)}
            </div>
          </section>

          {/* Extra */}
          {(user.tax_regime || user.iban || user.notes) && (
            <section>
              <h4 className="font-semibold mb-2">Altre informazioni</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pro-detail-extras">
                {line("Regime fiscale", user.tax_regime)}
                {line("IBAN", user.iban)}
                {user.notes && <div className="md:col-span-2">{line("Note", user.notes)}</div>}
              </div>
            </section>
          )}

          {/* Metadata */}
          <section>
            <h4 className="font-semibold mb-2">Metadati account</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pro-detail-meta">
              {line("Ruolo", user.role)}
              {line("Registrato il", user.created_at?.slice(0, 10))}
              {line("Ultimo aggiornamento", user.updated_at?.slice(0, 10))}
              {line("Ultimo accesso", user.last_login?.slice(0, 16)?.replace("T", " "))}
              {line("Approvato il", user.approved_at?.slice(0, 10))}
              {line("Approvato da", user.approved_by_email || user.approved_by)}
            </div>
          </section>

          <section className="border-t pt-3">
            <VisibleServicesPicker hiddenIds={hiddenSvc} onChange={setHiddenSvc} testIdPrefix="pro-detail" />
          </section>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={onClose} data-testid="pro-detail-close">Chiudi</Button>
          <Button
            onClick={save}
            disabled={!isDirty || saving}
            className="ht-gradient border-0"
            data-testid="pro-detail-save"
          >
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

